import type {
  ImportInfo,
  RetryImportRequest,
  StartImportRequest,
  StoreApplyRequest,
} from '@mcgames/contracts';
import { useQueryClient } from '@tanstack/react-query';
import { useApiMutation, useApiQuery } from '../../api/hooks.ts';
import { importPollInterval } from '../indexer/indexer-hooks.ts';
import { gamePath } from './game-hooks.ts';

const DAY_MS = 24 * 60 * 60 * 1000;

export function latestImportPath(gameId: string): string {
  return `${gamePath(gameId)}/import`;
}

/** The latest import of the game, or `null`; refetched every 3 s while it runs. */
export function useLatestImport(gameId: string) {
  return useApiQuery<ImportInfo | null>(latestImportPath(gameId), {
    refetchInterval: (query) => importPollInterval(query.state.data),
  });
}

/** Whether the panel shows `info`: unfinished imports always, finished ones for 24 hours. */
export function isImportShown(info: ImportInfo | null | undefined, now = Date.now()): boolean {
  if (info === null || info === undefined) return false;
  if (info.status === 'running' || info.status === 'needs_review' || info.status === 'failed') {
    return true;
  }
  return now - new Date(info.updatedAt).getTime() < DAY_MS;
}

/**
 * Refetches the game and its latest import; call after something changed an import. The promise
 * settles when both are refetched, so returning it from `onSuccess` keeps a mutation pending until
 * the page shows the new state.
 */
export function useRefreshGameImport(gameId: string): () => Promise<void> {
  const queryClient = useQueryClient();
  return async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: [gamePath(gameId)] }),
      queryClient.invalidateQueries({ queryKey: [latestImportPath(gameId)] }),
    ]);
  };
}

/** Imports an indexer release as a new version of the game. */
export function useAddReleaseVersion(gameId: string) {
  const refresh = useRefreshGameImport(gameId);
  return useApiMutation<StartImportRequest, ImportInfo>('POST', `${gamePath(gameId)}/imports`, {
    onSuccess: () => refresh(),
  });
}

export function importActionPath(importId: string, action: string): string {
  return `/api/admin/imports/${encodeURIComponent(importId)}/${action}`;
}

/** Retries a stopped import; `installSteps` resolve a stop at detection. */
export function useRetryImport(gameId: string, importId: string) {
  const refresh = useRefreshGameImport(gameId);
  return useApiMutation<RetryImportRequest | void, ImportInfo>(
    'POST',
    importActionPath(importId, 'retry'),
    { onSuccess: () => refresh() },
  );
}

export function useChooseCandidate(gameId: string, importId: string) {
  const refresh = useRefreshGameImport(gameId);
  return useApiMutation<StoreApplyRequest, ImportInfo>(
    'POST',
    importActionPath(importId, 'candidate'),
    { onSuccess: () => refresh() },
  );
}
